import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Card,
  CardHeader,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import { Product } from "features/Products/types/product.types";
import {
  deleteProduct,
  getProducts,
} from "features/Products/services/ProductService";
import { toast } from "react-toastify";
import DeleteModal from "components/common/DeleteModal";
import { ColumnDef } from "@tanstack/react-table";
import { IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import { getDocumentTitle } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useDebounce } from "components/hooks/useDebounce";
import ProductListFilters from "./ProductListFilters";

function ProductThumb({ src }: { src?: string }) {
  const [showPlaceholder, setShowPlaceholder] = useState(!src);

  if (showPlaceholder) {
    return (
      <div
        className="flex-shrink-0 rounded border bg-light-subtle d-flex align-items-center justify-content-center"
        style={{ width: 48, height: 48 }}
        aria-hidden
      >
        <i className="ri-image-2-line text-muted fs-20" />
      </div>
    );
  }

  return (
    <div
      className="flex-shrink-0 rounded border overflow-hidden bg-light"
      style={{ width: 48, height: 48 }}
    >
      <img
        src={src}
        alt=""
        className="w-100 h-100"
        style={{ objectFit: "cover" }}
        onError={() => setShowPlaceholder(true)}
      />
    </div>
  );
}

function getTotalStock(product: Product): number | null {
  const total = product.stock?.totalAvailableQty ?? product.totalAvailableQty;
  return typeof total === "number" && Number.isFinite(total) ? total : null;
}

const ProductList = (props: any) => {
  document.title = getDocumentTitle("Products");
  const { can } = useAccess();

  const tableRef = useRef<() => void>(null);
  const [product, setProduct] = useState<Product>();
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const [isActiveFilter, setIsActiveFilter] = useState<"" | "true" | "false">(
    "",
  );
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [productTypeId, setProductTypeId] = useState<number | null>(null);
  const [materialId, setMaterialId] = useState<number | null>(null);
  const [uomId, setUomId] = useState<number | null>(null);
  const [brandFilter, setBrandFilter] = useState("");
  const [manufacturerFilter, setManufacturerFilter] = useState("");
  const debouncedBrand = useDebounce(brandFilter, 400);
  const debouncedManufacturer = useDebounce(manufacturerFilter, 400);

  const requestParams = useMemo(
    () => ({
      categoryId: categoryId ?? undefined,
      productTypeId: productTypeId ?? undefined,
      materialId: materialId ?? undefined,
      uomId: uomId ?? undefined,
      isActive:
        isActiveFilter === "" ? undefined : isActiveFilter === "true",
      brand: debouncedBrand.trim() || undefined,
      manufacturer: debouncedManufacturer.trim() || undefined,
    }),
    [
      categoryId,
      productTypeId,
      materialId,
      uomId,
      isActiveFilter,
      debouncedBrand,
      debouncedManufacturer,
    ],
  );

  const filtersBadgeCount = useMemo(() => {
    let n = 0;
    if (categoryId != null) n += 1;
    if (productTypeId != null) n += 1;
    if (materialId != null) n += 1;
    if (uomId != null) n += 1;
    if (isActiveFilter) n += 1;
    if (debouncedBrand.trim()) n += 1;
    if (debouncedManufacturer.trim()) n += 1;
    return n;
  }, [
    categoryId,
    productTypeId,
    materialId,
    uomId,
    isActiveFilter,
    debouncedBrand,
    debouncedManufacturer,
  ]);

  const clearListFilters = useCallback(() => {
    setIsActiveFilter("");
    setCategoryId(null);
    setProductTypeId(null);
    setMaterialId(null);
    setUomId(null);
    setBrandFilter("");
    setManufacturerFilter("");
  }, []);

  const onClickDelete = useCallback((record: Product) => {
    setProduct(record);
    setDeleteModal(true);
  }, []);
  const handleDeleteProduct = async () => {
    if (product) {
      try {
        const response = await deleteProduct(product.id);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setDeleteModal(false);
      } catch (err) {
        console.warn("Error deleting product: ", err);
        toast.error((err as any).message);
      }
    }
  };

  const columns: ColumnDef<Product>[] = useMemo(
    () => [
      IndexColumn<Product>(),
      {
        header: "Product",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const p = row.original;
          const thumb = p.images?.[0];
          const typeName = p.type?.name;
          const categoryName = p.category?.name;

          return (
            <div className="d-flex align-items-start gap-3 min-w-0 py-1">
              <ProductThumb src={thumb} />
              <div className="flex-grow-1 min-w-0">
                <Link
                  to={`/inventory-management/products/${p.id}`}
                  className="text-body fw-semibold d-inline-block text-truncate"
                  style={{ maxWidth: "min(320px, 100%)" }}
                  title={p.name}
                >
                  {p.name}
                </Link>
                {p.sku ? (
                  <div className="mt-1">
                    <code className="user-select-all small text-muted bg-light px-2 py-0 rounded border">
                      {p.sku}
                    </code>
                  </div>
                ) : null}
                <div className="text-muted small mt-1 text-wrap">
                  {typeName ? (
                    <span title="Product type">
                      <i className="ri-price-tag-3-line me-1 align-middle" />
                      {typeName}
                    </span>
                  ) : (
                    <span className="fst-italic">No type</span>
                  )}
                  {categoryName ? (
                    <span className="ms-2" title="Category">
                      <i className="ri-folder-line me-1 align-middle" />
                      {categoryName}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        header: "On hand",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const totalStock = getTotalStock(row.original);
          if (totalStock == null) {
            return (
              <span
                className="text-muted"
                title="No stock quantities recorded for this product"
              >
                —
              </span>
            );
          }

          const color =
            totalStock <= 0 ? "danger" : totalStock < 5 ? "warning" : "success";
          return (
            <span
              className={`badge bg-${color}-subtle text-${color} rounded-pill`}
              title="Total available quantity across locations"
            >
              {totalStock}
            </span>
          );
        },
      },
      {
        id: "standardRate",
        header: () => (
          <span title="Standard list price per unit">List price</span>
        ),
        accessorKey: "standardRate",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rate = row.original.standardRate;
          return (
            <span className="fw-medium tabular-nums">
              {formatCurrency(rate ?? 0)}
            </span>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const val = row.original.isActive;
          const classname = val ? "success" : "danger";
          const text = val ? "Active" : "Inactive";
          return (
            <span
              className={`badge bg-${classname}-subtle text-${classname} rounded-pill`}
            >
              {text}
            </span>
          );
        },
      },
      {
        header: "Published",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return <DateTime date={row.original.created_at} />;
        },
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Product actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                <DropdownItem
                  tag={Link}
                  to={`/inventory-management/products/${record.id}`}
                >
                  <i className="ri-eye-line align-bottom me-2 text-muted" />
                  View
                </DropdownItem>
                <DropdownItem
                  tag={Link}
                  to={`/inventory-management/products/edit/${record.id}`}
                >
                  <i className="ri-pencil-line align-bottom me-2 text-muted" />
                  Edit
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem>
                  <i className="ri-stack-line align-bottom me-2 text-muted" />
                  Add opening stock
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem
                  className="text-danger"
                  onClick={() => onClickDelete(record)}
                >
                  <i className="ri-delete-bin-line align-bottom me-2" />
                  Delete
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [onClickDelete],
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteProduct}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Products" pageTitle="Inventory Management" />
          <Card>
            <CardHeader className="border-bottom">
              <div className="d-flex flex-column flex-sm-row justify-content-sm-between align-items-sm-center gap-2">
                <div>
                  <h5 className="card-title mb-1">Catalog</h5>
                  <p className="text-muted mb-0 small">
                    Search across catalog fields or use filters, then open a row
                    to view or edit.
                  </p>
                </div>
                {can(Permissions.PRODUCT.CREATE) ? (
                  <Link
                    to="/inventory-management/products/create"
                    className="btn btn-primary flex-shrink-0"
                  >
                    <i className="ri-add-line align-bottom me-1"></i> Add
                    product
                  </Link>
                ) : null}
              </div>
            </CardHeader>
            <div className="card-body pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getProducts}
                requestParams={requestParams}
                pageSize={10}
                globalSearch={true}
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
                SearchPlaceholder="Search SKU, name, brand, manufacturer, spec, category, material…"
                toolbarRight={<ProductListFilters
                  isActiveFilter={isActiveFilter}
                  onIsActiveChange={setIsActiveFilter}
                  categoryId={categoryId}
                  onCategoryIdChange={setCategoryId}
                  productTypeId={productTypeId}
                  onProductTypeIdChange={setProductTypeId}
                  materialId={materialId}
                  onMaterialIdChange={setMaterialId}
                  uomId={uomId}
                  onUomIdChange={setUomId}
                  brand={brandFilter}
                  onBrandChange={setBrandFilter}
                  manufacturer={manufacturerFilter}
                  onManufacturerChange={setManufacturerFilter}
                />}
                filterCount={filtersBadgeCount}
                onFiltersClear={clearListFilters}
              />
            </div>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProductList;
